import Icon from './Icon';

function StatusLine({ item, active = false, onNavigate }) {
  return (
    <button
      type="button"
      className={`delivery-workspace__status delivery-workspace__status--${item.status}${active ? ' is-active' : ''}`}
      onClick={() => onNavigate(item.id)}
      aria-current={active ? 'true' : undefined}
    >
      <span className="delivery-workspace__status-mark" aria-hidden />
      <span className="delivery-workspace__status-copy">
        <strong>{item.label}</strong>
        <small>{item.statusLabel}</small>
      </span>
    </button>
  );
}

function ProgramSummary({ summary }) {
  const entries = [
    [summary.mealsPerDay || '—', 'repas / jour'],
    [summary.supplementGroups || '—', 'moments de prise'],
    [summary.fridgeItems || '—', 'essentiels frigo'],
    [summary.recipesReady || '—', 'recettes prêtes'],
  ];
  if (summary.mealPrepSessions > 0) {
    entries.push([summary.mealPrepSessions, 'sessions Meal Prep']);
  }
  return (
    <dl className="delivery-workspace__summary">
      {entries.map(([value, label]) => (
        <div key={label}>
          <dt>{value}</dt>
          <dd>{label}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function DeliveryFinalizationWorkspace({
  clientName,
  clientGreeting,
  packLabel,
  model,
  activeSection,
  onNavigate,
  onOpenFullPreview,
  fullPreviewOpen = false,
  preparationActive = false,
  onOpenPreparation,
  onClose,
  headerActions,
  notices,
  children,
  footer,
}) {
  const complete = model.summary.completeCount;
  const total = model.summary.totalCount;
  const remaining = model.pending.length;
  const activeItem = model.items.find((item) => item.id === activeSection);
  const activeLabel = preparationActive
    ? 'Dossier application'
    : (fullPreviewOpen ? 'Aperçu complet' : (activeItem?.label || 'Introduction'));

  return (
    <div className="delivery-workspace">
      <header className="delivery-workspace__topbar">
        <div className="delivery-workspace__title-group">
          <p>Finalisation du programme</p>
          <h2>{clientName}</h2>
          <span>{packLabel} · {complete}/{total} sections {preparationActive ? 'déjà dans Livraison' : 'prêtes'}</span>
        </div>
        <div className="delivery-workspace__top-actions">
          {headerActions}
          <button type="button" className="delivery-workspace__close" onClick={onClose} aria-label="Fermer l’espace de finalisation">
            ×
          </button>
        </div>
      </header>

      {notices}

      <div className="delivery-workspace__layout">
        <aside className="delivery-workspace__overview" aria-label="État de préparation">
          <section className="delivery-workspace__overview-block">
            <div className="delivery-workspace__section-heading">
              <p>{preparationActive ? 'Déjà dans Livraison' : 'État de préparation'}</p>
              <span>{complete}/{total}</span>
            </div>
            <div className="delivery-workspace__progress" aria-label={`${complete} sections prêtes sur ${total}`}>
              <span style={{ width: `${total > 0 ? (complete / total) * 100 : 0}%` }} />
            </div>
            {onOpenPreparation && (
              <button
                type="button"
                className={`delivery-workspace__preparation${preparationActive ? ' is-active' : ''}`}
                onClick={onOpenPreparation}
                aria-current={preparationActive ? 'true' : undefined}
              >
                <Icon type="folder" size={15} />
                <span>
                  <strong>Préparer le dossier</strong>
                  <small>4 lots · 7 sections · 1 application</small>
                </span>
              </button>
            )}
            <div className="delivery-workspace__status-list">
              {model.items.map((item) => (
                <StatusLine
                  key={item.id}
                  item={item}
                  active={activeSection === item.id}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          </section>

          <section className="delivery-workspace__overview-block delivery-workspace__overview-block--summary">
            <div className="delivery-workspace__section-heading">
              <p>Résumé</p>
            </div>
            <ProgramSummary summary={model.summary} />
          </section>

          <section className="delivery-workspace__overview-block delivery-workspace__todo">
            <div className="delivery-workspace__section-heading">
              <p>{remaining > 0 ? `${remaining} élément${remaining > 1 ? 's' : ''} à finaliser` : 'Finalisation'}</p>
            </div>
            {remaining === 0 ? (
              <p className="delivery-workspace__all-clear">
                <Icon type="check" size={14} /> Programme prêt pour publication.
              </p>
            ) : (
              <div className="delivery-workspace__todo-list">
                {model.pending.map((item) => (
                  <button type="button" key={`${item.id}-${item.label}`} onClick={() => onNavigate(item.id)}>
                    <span>
                      <strong>{item.label}</strong>
                      <small>{item.description}</small>
                    </span>
                    <span className="delivery-workspace__todo-action">{item.actionLabel} →</span>
                  </button>
                ))}
              </div>
            )}
          </section>
        </aside>

        <main className="delivery-workspace__content" id="delivery-workspace-content">
          <section className="delivery-workspace__client-summary" aria-label="Aperçu cliente">
            <div>
              <p>Aperçu réel de l’application</p>
              <h3>{clientGreeting || `Bonjour ${clientName},`}</h3>
              <span>{packLabel}</span>
              <span className="delivery-workspace__current-section">Section ouverte · {activeLabel}</span>
            </div>
            <ProgramSummary summary={model.summary} />
            <button type="button" className="delivery-workspace__preview-link" onClick={onOpenFullPreview}>
              {fullPreviewOpen ? 'Revenir à la vue ciblée ↑' : 'Ouvrir l’aperçu complet ↓'}
            </button>
          </section>
          {children}
        </main>

        <aside className="delivery-workspace__navigation" aria-label="Parcours de l’application cliente">
          <p className="delivery-workspace__nav-eyebrow">Application cliente</p>
          <h3>Parcours visible</h3>
          <p className="delivery-workspace__nav-intro">
            {preparationActive
              ? 'Ce que contient la fiche aujourd’hui. Le dossier en cours de préparation reste au centre tant qu’il n’est pas appliqué.'
              : 'Ordre exact des sections que la cliente parcourra.'}
          </p>
          <nav>
            {model.items.map((item, index) => (
              <button
                type="button"
                key={item.id}
                onClick={() => onNavigate(item.id)}
                aria-current={activeSection === item.id ? 'true' : undefined}
                className={activeSection === item.id ? 'is-active' : undefined}
              >
                <span className="delivery-workspace__nav-index">{String(index + 1).padStart(2, '0')}</span>
                <span className="delivery-workspace__nav-copy">
                  <strong>{item.label}</strong>
                  <small>{item.description}</small>
                </span>
                <span className={`delivery-workspace__nav-state delivery-workspace__nav-state--${item.status}`}>
                  {item.statusLabel}
                </span>
              </button>
            ))}
          </nav>
        </aside>
      </div>

      {footer && <div className="delivery-workspace__footer">{footer}</div>}
    </div>
  );
}
